/**
 * What a camera's encoder is actually doing, from WebRTC's own stats: the
 * rate it sends against the rate the camera captures, the codec and encoder,
 * and `qualityLimitationReason`, which names the bottleneck ('cpu' or
 * 'bandwidth') when the sent rate falls below the cap. Shown on the phone
 * camera page behind ?stats=1, and sampled for the board's own camera by
 * cameraPublishSampler.js (the runtime telemetry's publisher fields).
 * `id`, `timestamp`, `encoderFramesSent` and `encoderBytesSent` belong to the
 * encoder that carries the picture, so a caller can take deltas between two
 * reports. With `previousFrames` (stats id -> framesSent from the caller's last
 * report) that encoder is the one that sent the most SINCE then: after a camera
 * switch a stopped encoder can keep the larger lifetime count.
 *
 * @param {RTCStatsReport|Map|null} report - a publisher connection's getStats(),
 *   or LocalTrack.getRTCStatsReport()
 * @param {{previousFrames?: Map<string, number>}} [opts]
 * @returns {object|null}
 */
export function summarizeSenderStats(report, { previousFrames = null } = {}) {
  if (!report) return null;
  const all = [];
  report.forEach?.((entry) => all.push(entry));
  const byId = new Map(all.map((s) => [s.id, s]));
  const outbound = all.filter((s) => s.type === 'outbound-rtp' && (s.kind === 'video' || s.mediaType === 'video'));
  if (outbound.length === 0) return null;
  // With SVC one encoding carries every layer; with several, report the busiest.
  // The stream actually carrying the picture: with a backup codec there can be
  // two encoders, one of them idle, so pick the one that has sent the most.
  const lifetime = (s) => s.framesSent || 0;
  const recent = (s) => lifetime(s) - (previousFrames?.get(s.id) ?? 0);
  const score = previousFrames && outbound.some((s) => recent(s) > 0) ? recent : lifetime;
  const main = outbound.reduce((best, s) => (score(s) > score(best) ? s : best), outbound[0]);
  const source = all.find((s) => s.type === 'media-source' && s.kind === 'video');
  const pair = all.find((s) => s.type === 'candidate-pair' && (s.nominated || s.selected) && s.state === 'succeeded');
  const durations = main.qualityLimitationDurations || {};
  return {
    id: main.id,
    timestamp: typeof main.timestamp === 'number' ? main.timestamp : null,
    encoderFramesSent: main.framesSent ?? null,
    encoderBytesSent: main.bytesSent ?? null,
    sentFps: round(main.framesPerSecond),
    captureFps: round(source?.framesPerSecond),
    width: main.frameWidth || null,
    height: main.frameHeight || null,
    codec: byId.get(main.codecId)?.mimeType?.replace(/^video\//, '') || null,
    encoder: main.encoderImplementation || null,
    scalability: main.scalabilityMode || null,
    limitedBy: main.qualityLimitationReason || 'none',
    limitedSeconds: {
      cpu: round(durations.cpu),
      bandwidth: round(durations.bandwidth),
    },
    targetKbps: main.targetBitrate ? Math.round(main.targetBitrate / 1000) : null,
    availableKbps: pair?.availableOutgoingBitrate ? Math.round(pair.availableOutgoingBitrate / 1000) : null,
    rttMs: pair?.currentRoundTripTime != null ? Math.round(pair.currentRoundTripTime * 1000) : null,
    bytesSent: outbound.reduce((sum, s) => sum + (s.bytesSent || 0), 0),
    framesSent: outbound.reduce((sum, s) => sum + (s.framesSent || 0), 0),
    framesEncoded: outbound.reduce((sum, s) => sum + (s.framesEncoded || 0), 0),
  };
}

function round(n) {
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n * 10) / 10 : null;
}
